import React from "react";

interface PendingProductCardProps {
  product: any;
  onViewDetails: (product: any) => void;
  showStatus?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}

const PendingProductCard: React.FC<PendingProductCardProps> = ({
  product,
  onViewDetails,
  showStatus = false,
  selected = false,
  onToggleSelect,
}) => {
  const getStatusStyle = (status: string) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-emerald-50 text-emerald-700 border border-emerald-100";
      case "rejected":
        return "bg-red-50 text-red-600 border border-red-100";
      case "under-review":
        return "bg-purple-50 text-purple-600 border border-purple-100";
      case "pending":
      default:
        return "bg-amber-50 text-amber-600 border border-amber-100";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      case "under-review":
        return "Under Review";
      case "pending":
      default:
        return "Pending";
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden flex flex-col h-full hover:shadow-md hover:border-gray-300 transition-all cursor-pointer"
      onClick={() => onViewDetails(product)}
    >
      <div className="p-3 flex justify-between items-start border-b border-gray-50 bg-gray-50/50">
        <div className="flex items-start gap-2">
          {onToggleSelect && (
            <button
              type="button"
              className="flex items-center justify-center pt-[2px]"
              onClick={(e) => {
                e.stopPropagation();
                if (!product?._id) return;
                onToggleSelect(String(product._id));
              }}
              aria-label={selected ? "Deselect product" : "Select product"}
              title={selected ? "Deselect" : "Select"}
            >
              <input
                type="checkbox"
                checked={selected}
                onChange={() => undefined}
                className="h-3.5 w-3.5 accent-secondary cursor-pointer rounded-sm"
              />
            </button>
          )}
          <h3 className="font-semibold text-gray-800 text-[13px] leading-tight line-clamp-1">
            {product.name} <span className="text-gray-400 font-normal text-[11px] ml-0.5">{product.measurement}</span>
          </h3>
        </div>

        <span
          className={`text-[9px] font-bold px-1.5 py-[2px] rounded uppercase tracking-wide whitespace-nowrap ${getStatusStyle(
            product.status,
          )}`}
        >
          {getStatusLabel(product.status)}
        </span>
      </div>

      <div className="flex-1 bg-white flex items-center justify-center p-3 min-h-[110px]">
        <img
          src={product.image || product.images?.[0] || "/placeholder-image.png"}
          alt={product.name}
          className="max-h-20 object-contain drop-shadow-sm transition-transform hover:scale-105"
        />
      </div>

      <div className="p-3 pt-2 bg-gray-50/30">
        <div className="flex justify-between items-center mb-1.5">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            {product.type || "--"}
          </span>
          <span className="text-[14px] font-bold text-secondary">
            {typeof product.price === "number"
              ? `₦${product.price.toLocaleString()}`
              : "--"}
          </span>
        </div>

        <div className="flex items-center gap-1 mt-1.5">
          <span className="text-[10px] text-gray-400">By:</span>
          <span className="text-[10px] font-medium text-gray-500 line-clamp-1">
            {typeof product.submittedBy === "object" && product.submittedBy !== null
              ? product.submittedBy.name || product.submittedBy.email || "Unknown Admin"
              : product.submittedBy || "Unknown Admin"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PendingProductCard;
